import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Card, CardHeader, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useState } from 'react';
import { useRootStore } from '../hooks/useRootStore';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Gender } from '../data/Gender';
import { CustomerEmployee } from '../data/CustomerEmployee';
import { User } from '../data/User';
import { CustomerEmployeeAccessLevel } from '../data/CustomerEmployeeAccessLevel';
import { AgentType } from '../data/AgentType';
import { EmployeeAccessLevel } from '../data/EmployeeAccessLevel';

export default function ChangeCustomerEmployeeDialog({ employee, onSave, onCancel }: { employee: CustomerEmployee | User | null, onSave: (changed: CustomerEmployee | User) => void, onCancel: () => void }) {
    const [changed, setChanged] = useState(employee ? { ...employee, Gender: employee.Gender ?? Gender.undisclosed } : { Gender: Gender.undisclosed } as User);

    const [firstNameNotSpecified, setFirstNameNotSpecified] = useState(false);
    const [lastNameNotSpecified, setLastNameNotSpecified] = useState(false);
    const [contactsNotSpecified, setContactsNotSpecified] = useState(false);
    const { api } = useRootStore();

    const save = async () => {
        let result = true;
        if ((changed?.FirstName?.trim() ?? '') == '') {
            setFirstNameNotSpecified(true);
            result = false;
        };
        if ((changed.LastName?.trim() ?? '') == '') {
            setLastNameNotSpecified(true);
            result = false;
        };
        if (((changed.Phone?.trim() ?? '') == '')
            && ((changed.Email?.trim() ?? '') == '')
            && ((changed.Skype?.trim() ?? '') == '')
            && ((changed.Telegram?.trim() ?? '') == '')) {
            setContactsNotSpecified(true);
            result = false;
        };

        if (result) {
            if (changed.ID) {
                await api.updateCustomerEmployee(changed).then(() => onSave(changed));
            } else {
                await api.createCustomerEmployee(changed).then((newCustomerEmployee) => onSave(newCustomerEmployee));
            }
        }
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Dialog
                maxWidth='lg'
                fullWidth
                open={true}                
                onClose={onCancel}
            >
                <DialogTitle>Изменение профиля</DialogTitle>
                <DialogContent className='change-employee-dialog' sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Card className="inputs" sx={{ flex:1, display: 'flex', flexDirection: "column" }}>
                        <CardHeader title="Основные данные" className="card-header" />
                        <Box><TextField error={lastNameNotSpecified} label="Фамилия" value={changed.LastName ?? ''} onChange={(e) => { setLastNameNotSpecified(false); setChanged({ ...changed, LastName: e.target.value }); } } /></Box>
                        <Box><TextField error={firstNameNotSpecified} label="Имя" value={changed.FirstName ?? ''} onChange={(e) => { setFirstNameNotSpecified(false); setChanged({ ...changed, FirstName: e.target.value }); }} /></Box>
                        <Box><TextField label="Отчество" value={changed.MiddleName ?? ''} onChange={(e) => setChanged({ ...changed, MiddleName: e.target.value })} /></Box>
                        <Box>
                            <FormControl fullWidth>
                                <InputLabel id="gender-select-label">Пол</InputLabel>
                                <Select
                                    labelId="gender-select-label"
                                    id="gender-select"
                                    value={changed.Gender?.toString() ?? Gender.undisclosed}
                                    label="Пол"
                                    onChange={e => setChanged({ ...changed, Gender: e.target.value as unknown as number })}
                                >
                                    <MenuItem value={Gender.male}>Мужской</MenuItem>
                                    <MenuItem value={Gender.female}>Женский</MenuItem>
                                    <MenuItem value={Gender.undisclosed}>Не скажу</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box><TextField label="Должность" value={(changed as CustomerEmployee).Position ?? ''} onChange={(e) => setChanged({ ...changed, Position: e.target.value})} /></Box>
                    </Card>
                    <Card className="inputs" sx={{ flex: 1, display: 'flex', flexDirection: "column", border: (contactsNotSpecified ? '1px solid red !important;' : '')}}>
                        <CardHeader title="Контакты" className="card-header" />
                        <Box><TextField label="Телефон" value={changed.Phone ?? ''} onChange={(e) => { setContactsNotSpecified(false); setChanged({ ...changed, Phone: e.target.value }); } } /></Box>
                        <Box><TextField label="Email" value={changed.Email ?? ''} onChange={(e) => { setContactsNotSpecified(false); setChanged({ ...changed, Email: e.target.value }); }} /></Box>
                        <Box><TextField label="Skype" value={changed.Skype ?? ''} onChange={(e) => { setContactsNotSpecified(false); setChanged({ ...changed, Skype: e.target.value }); }} /></Box>
                        <Box><TextField label="Телеграм" value={changed.Telegram ?? ''} onChange={(e) => { setContactsNotSpecified(false); setChanged({ ...changed, Telegram: e.target.value }); }} /></Box>
                    </Card>
                    <Card className="inputs" sx={{ flex: 1, display: 'flex', flexDirection: "column", border: (contactsNotSpecified ? '1px solid red !important;' : '') }}>
                        <CardHeader title="Доступ к трекеру" className="card-header" />
                        <Box><TextField label="Логин" value={(changed as User).Login ?? ''} onChange={(e) => setChanged({ ...changed, Login: e.target.value })} /></Box>                        
                        <FormControl fullWidth sx={{position:'relative'}}>
                            <Box sx={{ position: 'relative' }}>
                                <InputLabel id="access-select-label">Доступ к трекеру</InputLabel>
                                <Select
                                    labelId="access-select-label"
                                    id="access-select"
                                    disabled={((changed as User).Login?.trim() ?? '') == ''}
                                    value={(((changed as User).Login?.trim() ?? '') == '') ? CustomerEmployeeAccessLevel.none : (changed as User).AccessLevelID}
                                    label="Доступ к трекеру"
                                    onChange={e => {
                                        setChanged({
                                            ...changed,
                                            AccessLevelID: (changed.AgentType == AgentType.Employee) ? e.target.value as unknown as number as EmployeeAccessLevel : e.target.value as unknown as number as CustomerEmployeeAccessLevel
                                        })
                                    }}
                                >
                                    <MenuItem value={CustomerEmployeeAccessLevel.none}>Без доступа</MenuItem>
                                    <MenuItem value={CustomerEmployeeAccessLevel.reporter}>Заявитель</MenuItem>
                                    <MenuItem value={CustomerEmployeeAccessLevel.fullAccess}>Полный доступ</MenuItem>
                                </Select>
                            </Box>
                        </FormControl>
                    </Card>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel}>Отмена</Button>
                    <Button onClick={save}>Сохранить</Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}